/**
 * WebSocket Feature Flag Configuration
 *
 * Controls gradual rollout of WebSocket real-time functionality
 * to replace HTTP polling for event aggregation.
 *
 * Rollout Strategy:
 * 1. Staging: All users (testing phase)
 * 2. Production: Beta users only (monitor for 3 days)
 * 3. Production: All users (full rollout)
 *
 * Rollback: Set enabled=false to immediately fallback to HTTP polling
 */

const WEBSOCKET_CONFIG = {
  // Master feature flag - set to false to disable WebSocket globally
  // Default: true (enabled by default, can be disabled via env var)
  enabled: process.env.REACT_APP_WEBSOCKET_ENABLED !== 'false',

  // WebSocket API endpoint
  url: process.env.REACT_APP_WEBSOCKET_URL || '',

  // Beta users (Cognito usernames) - only these users get WebSocket during rollout
  // Empty array = all users
  betaUsers: [
    // Add Cognito usernames here for beta testing
    // 'john.doe',
    // 'jane.smith',
  ],

  // Rollout mode: 'disabled', 'beta', 'all'
  mode: process.env.REACT_APP_WEBSOCKET_ROLLOUT_MODE || 'disabled',

  // Connection settings
  reconnect: {
    enabled: true,
    maxAttempts: 5,
    delays: [1000, 2000, 5000, 10000, 30000], // Exponential backoff
  },

  // Heartbeat settings
  heartbeat: {
    enabled: true,
    interval: 30000, // 30 seconds
    timeout: 5000, // 5 seconds
  },

  // Fallback to HTTP polling if WebSocket fails
  fallbackToPolling: {
    enabled: true,
    afterFailures: 3, // Switch to polling after 3 connection failures
  },

  // Monitoring and debugging
  debug: process.env.REACT_APP_WEBSOCKET_DEBUG === 'true',
  logConnectionEvents: true,
  logMessages: process.env.NODE_ENV === 'development',
};

/**
 * Check if WebSocket should be enabled for a given user
 * @param {string} username - Cognito username
 * @returns {boolean}
 */
export function isWebSocketEnabledForUser(username) {
  // WebSocket globally disabled
  if (!WEBSOCKET_CONFIG.enabled) {
    return false;
  }

  // No WebSocket URL configured
  if (!WEBSOCKET_CONFIG.url) {
    console.warn('WebSocket URL not configured, falling back to polling');
    return false;
  }

  // Check rollout mode
  switch (WEBSOCKET_CONFIG.mode) {
    case 'disabled':
      return false;

    case 'beta':
      // Only beta users
      if (WEBSOCKET_CONFIG.betaUsers.length === 0) {
        console.warn('Beta mode but no beta users configured');
        return false;
      }
      return WEBSOCKET_CONFIG.betaUsers.includes(username);

    case 'all':
      // All users
      return true;

    default:
      console.warn(`Unknown rollout mode: ${WEBSOCKET_CONFIG.mode}`);
      return false;
  }
}

/**
 * Get connection configuration
 * @returns {object}
 */
export function getWebSocketConfig() {
  return {
    url: WEBSOCKET_CONFIG.url,
    reconnect: WEBSOCKET_CONFIG.reconnect,
    heartbeat: WEBSOCKET_CONFIG.heartbeat,
    fallbackToPolling: WEBSOCKET_CONFIG.fallbackToPolling,
    debug: WEBSOCKET_CONFIG.debug,
    logConnectionEvents: WEBSOCKET_CONFIG.logConnectionEvents,
    logMessages: WEBSOCKET_CONFIG.logMessages,
  };
}

/**
 * Check if we should fallback to HTTP polling
 * @param {number} consecutiveFailures - Number of consecutive connection failures
 * @returns {boolean}
 */
export function shouldFallbackToPolling(consecutiveFailures) {
  if (!WEBSOCKET_CONFIG.fallbackToPolling.enabled) {
    return false;
  }

  return consecutiveFailures >= WEBSOCKET_CONFIG.fallbackToPolling.afterFailures;
}

/**
 * Log WebSocket event (respects debug settings)
 * @param {string} event - Event name
 * @param {any} data - Event data
 */
export function logWebSocketEvent(event, data) {
  if (WEBSOCKET_CONFIG.logConnectionEvents) {
    console.log(`[WebSocket] ${event}:`, data);
  }
}

/**
 * Log WebSocket message (respects debug settings)
 * @param {string} direction - 'incoming' or 'outgoing'
 * @param {any} message - Message data
 */
export function logWebSocketMessage(direction, message) {
  if (WEBSOCKET_CONFIG.logMessages) {
    console.log(`[WebSocket] ${direction}:`, message);
  }
}

export default WEBSOCKET_CONFIG;
